'use client'

import { m } from 'framer-motion'
import {
  ArrowUpRight,
  Clock,
  Globe,
  type LucideIcon,
  Mail,
  MapPin,
  Phone,
  Share2,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { COMPANY_ADDRESS, COMPANY_EMAIL, COMPANY_PHONE, COMPANY_PHONE_RAW } from '@/lib/seo'

type ContactItem = {
  id: string
  col: string
  icon: LucideIcon
  title: string
  value: ReactNode
  sub?: string
  href: string
  action: string
  gradient: string
}

const items: ContactItem[] = [
  {
    id: 'email',
    col: 'md:col-span-2 lg:col-span-2',
    icon: Mail,
    title: 'Сотрудничество',
    value: COMPANY_EMAIL,
    href: `mailto:${COMPANY_EMAIL}`,
    action: 'Написать',
    gradient: 'from-blue-500/20 to-cyan-500/20',
  },
  {
    id: 'phone',
    col: 'md:col-span-1 lg:col-span-1',
    icon: Phone,
    title: 'Телефон',
    value: COMPANY_PHONE,
    href: `tel:${COMPANY_PHONE_RAW}`,
    action: 'Позвонить',
    gradient: 'from-purple-500/20 to-pink-500/20',
  },
  {
    id: 'office',
    col: 'md:col-span-1 lg:col-span-1',
    icon: MapPin,
    title: 'Офис',
    value: (
      <>
        {COMPANY_ADDRESS.city},
        <wbr /> {COMPANY_ADDRESS.street}
      </>
    ),
    sub: COMPANY_ADDRESS.region,
    href: '#map',
    action: 'На карте',
    gradient: 'from-emerald-500/20 to-teal-500/20',
  },
  {
    id: 'social',
    col: 'md:col-span-2 lg:col-span-2',
    icon: Share2,
    title: 'Социальные сети',
    value: '@digital_horizon',
    href: '#',
    action: 'Подписаться',
    gradient: 'from-orange-500/20 to-red-500/20',
  },
]

export default function ContactsInfo() {
  return (
    <section className="relative z-10 py-14 sm:py-20">
      <div className="home-container">
        <div className="grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((item, i) => (
            <m.a
              key={item.id}
              href={item.href}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className={`group relative overflow-hidden rounded-[28px] border border-white/5 bg-[#0c1220]/80 p-6 sm:rounded-[40px] sm:p-8 lg:p-10 ${item.col} block transition-all duration-500 hover:-translate-y-1 hover:border-white/20 hover:shadow-[0_0_80px_rgba(0,0,0,0.6)]`}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 blur-2xl transition-opacity duration-700 group-hover:opacity-100`}
              />

              <div className="relative z-10 flex h-full min-h-[200px] flex-col justify-between sm:min-h-[220px]">
                <div className="flex items-start justify-between">
                  <div className="flex h-16 w-16 items-center justify-center rounded-3xl border border-white/10 bg-white/5 shadow-xl backdrop-blur-md transition-transform duration-500 group-hover:scale-110">
                    <item.icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="home-meta flex items-center gap-3 rounded-full border border-white/10 bg-black/20 px-4 py-2 text-gray-400 backdrop-blur-md transition-all group-hover:border-white/30 group-hover:text-white sm:px-5 sm:py-2.5">
                    {item.action}
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </div>

                <div>
                  {item.title ? (
                    <div className="home-meta mb-3 text-gray-500">{item.title}</div>
                  ) : null}
                  <div className="home-card-title leading-tight tracking-tight break-words text-white transition-transform duration-500 group-hover:translate-x-2">
                    {item.value}
                  </div>
                  {item.sub ? (
                    <div className="home-subnote mt-2 text-gray-400">{item.sub}</div>
                  ) : null}
                </div>
              </div>
            </m.a>
          ))}

          <m.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mt-6 rounded-[28px] bg-gradient-to-r from-transparent via-white/10 to-transparent p-[1px] sm:mt-8 sm:rounded-[40px] md:col-span-2 lg:col-span-3"
          >
            <div className="relative flex flex-col items-center justify-between gap-10 overflow-hidden rounded-[27px] bg-[#0a0f1a] p-7 text-center sm:gap-12 sm:rounded-[39px] sm:p-10 lg:flex-row lg:p-16 lg:text-left">
              <div className="pointer-events-none absolute top-1/2 left-1/2 h-[300px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/5 blur-[100px]" />

              <div className="relative z-10">
                <h3 className="home-card-title mb-4 text-white">Работаем по всему миру</h3>
                <p className="home-subnote max-w-xl leading-relaxed text-gray-400">
                  Наши специалисты распределены по разным часовым поясам, обеспечивая непрерывный
                  цикл разработки и поддержки 24/7.
                </p>
              </div>

              <div className="relative z-10 flex flex-wrap justify-center gap-10 sm:gap-12 md:gap-24">
                <div className="group flex flex-col items-center">
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-blue-500/20 bg-blue-500/10 transition-transform duration-500 group-hover:scale-110 sm:mb-6 sm:h-16 sm:w-16">
                    <Clock className="h-8 w-8 text-blue-400" />
                  </div>
                  <div className="home-card-title-sm mb-1 text-white">10:00 - 19:00</div>
                  <div className="home-meta text-gray-500">МСК / GMT+3</div>
                </div>

                <div className="group flex flex-col items-center">
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-purple-500/20 bg-purple-500/10 transition-transform duration-500 group-hover:scale-110 sm:mb-6 sm:h-16 sm:w-16">
                    <Globe className="h-8 w-8 text-purple-400" />
                  </div>
                  <div className="home-card-title-sm mb-1 text-white">Remote First</div>
                  <div className="home-meta text-gray-500">Culture</div>
                </div>
              </div>
            </div>
          </m.div>
        </div>
      </div>
    </section>
  )
}
